import React, { useState, useRef } from 'react';
import axiosInstance from '../../../../axiosConfig';
import axios from 'axios';
import styles from './InsightExplorer.module.css';
import { renderCategoryCharts } from './InsightExplorerChartUtils';
import SummaryDisplayChat from './SummaryDisplay_Chat';

const InsightExplorer = () => {
  const [customerRequest, setCustomerRequest] = useState('');
  // removed unused responseMessage state
  const [chatHistory, setChatHistory] = useState([]);
  const [displayChatHistory, setDisplayChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const source = useRef(axios.CancelToken.source());

  // Function to get default date range (1 year back from today)
  const getDefaultDateRange = (chartData) => {
    if (!chartData || !chartData.dates || chartData.dates.length === 0) {
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      return {
        from: oneYearAgo.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };
    }
    
    const latestDate = new Date(Math.max(...chartData.dates.map(d => new Date(d))));
    const oneYearAgo = new Date(latestDate);
    oneYearAgo.setFullYear(latestDate.getFullYear() - 1);
    
    // Find the earliest available date that's not older than one year ago
    const earliestAvailable = new Date(Math.min(...chartData.dates.map(d => new Date(d))));
    const fromDate = earliestAvailable > oneYearAgo ? earliestAvailable : oneYearAgo;
    
    return {
      from: fromDate.toISOString().split('T')[0],
      to: latestDate.toISOString().split('T')[0]
    };
  };

  // Helper: get index of latest assistant message with chart
  const getLastAssistantChartIndex = (history) => {
    for (let i = history.length - 1; i >= 0; i--) {
      const m = history[i];
      if (m.role === 'assistant' && m.chartData) return i;
    }
    return -1;
  };

  // Function to handle date range changes for the latest chart only
  const handleLatestDateRangeChange = (field, value) => {
    setDisplayChatHistory(prev => {
      const idx = getLastAssistantChartIndex(prev);
      if (idx === -1) return prev;
      const updated = [...prev];
      const msg = updated[idx];
      const currentRange = msg.chartDateRange || getDefaultDateRange(msg.chartData);
      updated[idx] = {
        ...msg,
        chartDateRange: {
          ...currentRange,
          [field]: value
        }
      };
      return updated;
    });
  };

  // Reset date range for the latest chart to its default
  const resetLatestDateRange = () => {
    setDisplayChatHistory(prev => {
      const idx = getLastAssistantChartIndex(prev);
      if (idx === -1) return prev;
      const updated = [...prev];
      const msg = updated[idx];
      const defaultRange = getDefaultDateRange(msg.chartData);
      updated[idx] = {
        ...msg,
        chartDateRange: defaultRange
      };
      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Prevent multiple submissions while loading
    if (loading) return;
    
    // Simple debouncing - prevent requests within 2 seconds of each other
    const now = Date.now();
    if (now - lastRequestTime < 2000) {
      return;
    }
    
    setLoading(true);
    setLastRequestTime(now);
    
    try {
      source.current.cancel('Operation canceled by the user.');
      source.current = axios.CancelToken.source();

      const response = await axiosInstance.post('/chat_with_insight_explorer/', {
        customer_request: customerRequest,
        chat_history: chatHistory,
      }, {
        cancelToken: source.current.token,
      });
  
      // Handle both old string format and new object format
      let analysisContent;
      let chartData = null;
  // removed unused timeframeReasoning variable

      if (typeof response.data === 'string') {
        // Old format - just a string response
        analysisContent = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // New format - object with analysis, chart_data, etc.
        analysisContent = response.data.analysis || response.data.response || 'No analysis available';
        chartData = response.data.chart_data; // New: raw data for plotting
        // timeframeReasoning removed (was unused)
        
        // Check if we got an empty response due to backend async issues
        if (!response.data.analysis && !response.data.response && !response.data.error) {
          analysisContent = 'Server processing issue. Please try again in a moment.';
        }
        
        // chartData is stored per-message; date range will be attached to the message
      } else {
        analysisContent = 'No response received';
      }

      setChatHistory([
        ...chatHistory,
        { role: 'user', content: customerRequest },
        { role: 'assistant', content: analysisContent }, 
      ]);

      const newMessages = [...displayChatHistory];
      newMessages.push({ role: 'user', content: customerRequest });
      newMessages.push({
        role: 'assistant',
        content: analysisContent,
        chartData: chartData || null,
        chartDateRange: chartData ? getDefaultDateRange(chartData) : undefined
      });
      setDisplayChatHistory(newMessages);
  setCustomerRequest('');
    } catch (error) {
      // Improved error handling - add error message to chat history instead of responseMessage
      const errorMessage = error.response ? 
        (error.response.data.error || "Server error occurred") : 
        "Network error occurred";
      
      setDisplayChatHistory([
        ...displayChatHistory,
        { role: 'user', content: customerRequest },
        { 
          role: 'assistant', 
          content: `⚠️ Error: ${errorMessage}`,
          chartData: null
        },
      ]);
  setCustomerRequest('');
    }
    setLoading(false);
  };

  const handleChatButtonClick = () => {
    setShowChat(!showChat); 
    if (showChat) { 
      setChatHistory([]); 
      setDisplayChatHistory([]); 
    }
  };

  // Determine the latest assistant message index (regardless of chart)
  const lastAssistantIndex = (() => {
    for (let i = displayChatHistory.length - 1; i >= 0; i--) {
      if (displayChatHistory[i].role === 'assistant') return i;
    }
    return -1;
  })();

  return (
    <div className={styles.insightExplorer}>
      <div>
        <button onClick={handleChatButtonClick} className={styles.chatButton}>Chat</button> {/* New Chat button */}
        {showChat && ( 
          <>
            <div className={styles.chatContainer}>
              {displayChatHistory.map((message, index) => (
                <div key={index} className={styles.message} data-role={message.role}>
                  <span className={styles.messageLabel}>
                    {message.role === 'user' ? 'You' : 'Insight Explorer'}
                  </span>
                  <div className={styles.messageBubble}>
                    {message.role === 'user' ? (
                      message.content
                    ) : (
                      <div>
                        <SummaryDisplayChat content={message.content} />
                        {message.chartData && (
                          <div className={styles.chartContainer} style={{ marginTop: '16px' }}>
                            <div style={{
                              marginBottom: '12px',
                              padding: '10px 14px',
                              background: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              flexWrap: 'wrap'
                            }}>
                              <strong style={{ fontSize: '14px', color: '#ffffff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                Charts
                              </strong>
                              {index === lastAssistantIndex && (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <label style={{ fontWeight: '500', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>From:</label>
                                    <input
                                      type="date"
                                      value={message.chartDateRange?.from || ''}
                                      onChange={(e) => handleLatestDateRangeChange('from', e.target.value)}
                                      style={{ padding: '3px 7px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', fontSize: '12px', background: 'rgba(255,255,255,0.15)', color: '#fff' }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <label style={{ fontWeight: '500', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>To:</label>
                                    <input
                                      type="date"
                                      value={message.chartDateRange?.to || ''}
                                      onChange={(e) => handleLatestDateRangeChange('to', e.target.value)}
                                      style={{ padding: '3px 7px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', fontSize: '12px', background: 'rgba(255,255,255,0.15)', color: '#fff' }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={resetLatestDateRange}
                                    style={{ padding: '3px 10px', border: '1px solid rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                                  >
                                    Reset
                                  </button>
                                </div>
                              )}
                            </div>
                            {renderCategoryCharts(message.chartData, message.chartDateRange, styles)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                <textarea
                  value={customerRequest}
                  onChange={e => { setCustomerRequest(e.target.value); }}
                  placeholder="Message Insight Explorer…"
                  rows="4"
                  className={styles.textarea}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button type="submit" disabled={loading} className={styles.submitButton}>
                    {loading ? 'Thinking…' : 'Send'}
                  </button>
                  {loading && (
                    <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                      Analyzing…
                    </span>
                  )}
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InsightExplorer;
